import React, { useEffect, useRef, useState } from "react";

/**
 * Barre de progression ESTIMÉE pour les opérations longues sans télémétrie de
 * progression côté serveur (confirmation du classeur ≈ 3 min, promotion
 * production ≈ 1 min). La barre avance linéairement jusqu'à 90 % sur la durée
 * estimée puis y reste (« finalisation… ») : elle ne peut jamais mentir en
 * affichant 100 % avant la fin réelle. Quand l'appel serveur se termine,
 * elle saute à 100 %, marque un court temps d'arrêt pour que l'œil enregistre
 * la complétion, puis disparaît.
 */
export const EstimatedProgressBar: React.FC<{
	/** L'opération est-elle en cours ? Le montage/démontage est géré ici. */
	active: boolean;
	/** Durée totale estimée, en secondes (90 % atteints à ce moment-là). */
	estimatedSeconds: number;
	/** Libellé au-dessus de la barre, ex. « Synchronisation du classeur… ». */
	label: string;
}> = ({ active, estimatedSeconds, label }) => {
	const [elapsed, setElapsed] = useState(0);
	const [finishing, setFinishing] = useState(false);
	const wasActive = useRef(false);

	useEffect(() => {
		if (active) {
			wasActive.current = true;
			setFinishing(false);
			setElapsed(0);
			const startedAt = Date.now();
			const timer = window.setInterval(() => setElapsed((Date.now() - startedAt) / 1000), 500);
			return () => window.clearInterval(timer);
		}
		// Fin réelle : 100 % pendant un battement, puis retrait de la barre.
		if (wasActive.current) {
			setFinishing(true);
			const timeout = window.setTimeout(() => {
				setFinishing(false);
				wasActive.current = false;
			}, 1200);
			return () => window.clearTimeout(timeout);
		}
		return undefined;
	}, [active]);

	if (!active && !finishing) return null;

	const percent = finishing ? 100 : Math.min(90, (elapsed / estimatedSeconds) * 90);
	const holding = !finishing && percent >= 90;
	const minutes = Math.floor(elapsed / 60);
	const seconds = Math.floor(elapsed % 60);

	return (
		<div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" role="status" aria-live="polite">
			<div className="flex items-center justify-between gap-3">
				<p className="text-sm font-semibold text-slate-800">{label}</p>
				<p className="text-xs font-medium tabular-nums text-slate-500">
					{finishing
						? "Terminé"
						: holding
							? `Finalisation… (${minutes}:${String(seconds).padStart(2, "0")})`
							: `${minutes}:${String(seconds).padStart(2, "0")} / ≈ ${Math.round(estimatedSeconds / 60)} min`}
				</p>
			</div>
			<div className="mt-2.5 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
				<div
					className={`h-full rounded-full transition-all duration-500 ease-out ${
						finishing ? "bg-emerald-500" : "bg-[#0095DB]"
					} ${holding ? "animate-pulse" : ""}`}
					style={{ width: `${percent}%` }}
				/>
			</div>
			<p className="mt-2 text-xs text-slate-500">
				{finishing
					? "Synchronisation terminée."
					: "Ne fermez pas cet onglet : l'import remplace les données puis republie l'aperçu et les widgets."}
			</p>
		</div>
	);
};
